// ============================================================================
// Doc Order — §800 write-back mapping (the P2+P3 combined drop's write moment).
// Builds the grid outbox from the APPROVED closing state at send-to-lo (round-16
// finding 1: submit carries NO writes — Closing can still edit after submit).
//
// Ops rules (README §8.13–8.15, field-map payer buckets):
//   Borrower + PAC (collected at closing)      → borrowerPAC
//   Borrower + POC (borrower prepaid)          → borrowerPOC
//   LO/Lendwise + POC (absorbed lender-side)   → lenderPOC   (LO = Lender bucket)
//   LO/Lendwise + PAC + reimburse              → borrowerPAC (settles via recon)
//   $0 / notApplicable                         → never pushed
// Splits aggregate per bucket before writing. Manual fees land on the next
// empty misc line (808–818) — chosen from a live grid read at push time and
// persisted to write_back.assignedLine so re-pushes are idempotent.
//
// Vehicle (round-16 finding 5): these field/value pairs go through the
// doc-order-owned batch outbox (writeLoanFields), NOT pending_encompass_writes.
// Drain guarantees live in the edge fn (drainDocOrderFeeWritebacks — round-17).
// ============================================================================

import { DO_FEE_LINE_FIELDS, DO_MANUAL_FEE_LINES, type FeeLineId } from './field-map.ts';
import { moneyToNumber, type FieldValues } from './pull.ts';
import type { DocOrderFee, FeeSplit, PaidBy, FeeTiming } from './types.ts';

export interface GridWrite {
  fieldId: string;
  value: string;
}

export interface FeeWritePlan {
  feeId: string;
  feeKey: string;
  /** Line the writes target ('804' … '818'); manual fees get their assignment here. */
  line: FeeLineId;
  /** Newly assigned line for a manual fee (persist to write_back.assignedLine). */
  assignedLine?: FeeLineId;
  writes: GridWrite[];
}

export interface WritebackPlan {
  fees: FeeWritePlan[];
  /** Manual fees that could not be assigned (no empty misc line left). */
  unassigned: { feeId: string; name: string }[];
}

interface BucketTotals {
  borrowerPAC: number;
  borrowerPOC: number;
  lenderPOC: number;
}

/** One portion → its bucket per the ops table; null when nothing should write. */
function bucketOf(paidBy: PaidBy | null, timing: FeeTiming | null, reimburse: boolean): keyof BucketTotals | null {
  if (!paidBy || !timing) return null;
  if (paidBy === 'Borrower') return timing === 'POC' ? 'borrowerPOC' : 'borrowerPAC';
  // LO and Lendwise both map to the Lender bucket (ops #13)
  if (timing === 'POC') return 'lenderPOC';
  return reimburse ? 'borrowerPAC' : null; // lender-side PAC without reimburse doesn't occur in the model
}

/** Aggregate a fee's portions (splits[0..] or the unsplit whole) into bucket totals. */
export function feeBucketTotals(fee: DocOrderFee): BucketTotals {
  const totals: BucketTotals = { borrowerPAC: 0, borrowerPOC: 0, lenderPOC: 0 };
  const portions: FeeSplit[] =
    fee.splits && fee.splits.length >= 2
      ? fee.splits
      : [{ id: 'whole', amount: fee.amount, paidBy: fee.paidBy, timing: fee.timing, reimburse: fee.reimburse }];
  for (const p of portions) {
    const amt = Number(p.amount) || 0;
    if (amt <= 0) continue;
    const bucket = bucketOf(p.paidBy, p.timing, p.reimburse);
    if (bucket) totals[bucket] += amt;
  }
  return totals;
}

const money = (n: number) => n.toFixed(2);

export type AmountFieldTarget = 'borrowerDefault' | 'totalFee';

export interface WritebackOptions {
  /**
   * Which per-line field receives the fee amount. The live probe on the named
   * test loan confirms borrowerDefault vs totalFee before rollout — this is
   * the single switch (default = borrowerDefault per the ops field read).
   */
  amountField?: AmountFieldTarget;
}

/** Grid writes for one fee on a known line: amount + payer×timing portions + description/payee for misc lines. */
export function buildFeeGridWrites(fee: DocOrderFee, line: FeeLineId, opts: WritebackOptions = {}): GridWrite[] {
  const f = DO_FEE_LINE_FIELDS[line];
  const totals = feeBucketTotals(fee);
  const writes: GridWrite[] = [];
  const push = (id: string | number | undefined, value: string) => {
    if (id !== undefined && id !== null) writes.push({ fieldId: String(id), value });
  };

  // Amount write target is probe-confirmed (opts.amountField). Seller-side is
  // never written by the doc order; the payer×timing grid carries the
  // disposition portions.
  const amountField = opts.amountField ?? 'borrowerDefault';
  const borrowerTotal = totals.borrowerPAC + totals.borrowerPOC;
  push(f[amountField], money(borrowerTotal + totals.lenderPOC));
  push(f.borrowerPAC, money(totals.borrowerPAC));
  push(f.borrowerPOC, money(totals.borrowerPOC));
  push(f.lenderPOC, money(totals.lenderPOC));

  // Misc lines carry their own description + payee fields.
  if (f.feeDescription !== undefined && fee.name) push(f.feeDescription, fee.name);
  if (f.payee !== undefined && fee.payee) push(f.payee, fee.payee);

  return writes;
}

/**
 * Next empty misc line (808–818) from a live grid read: empty = blank/absent
 * description AND zero/absent total. `taken` excludes lines already assigned
 * to other manual fees in this same plan.
 */
export function nextEmptyMiscLine(gridValues: FieldValues, taken: Set<FeeLineId>): FeeLineId | null {
  for (const line of DO_MANUAL_FEE_LINES) {
    if (taken.has(line)) continue;
    const f = DO_FEE_LINE_FIELDS[line];
    const desc = f.feeDescription !== undefined ? (gridValues[String(f.feeDescription)] ?? '').trim() : '';
    const total = moneyToNumber(gridValues[String(f.totalFee)]) ?? 0;
    if (!desc && total <= 0) return line;
  }
  return null;
}

/** Text-valued grid write fields (misc-line description + payee) — everything
 *  else in the fee grid is money. Drives clearing values on re-push. */
const TEXT_WRITE_FIELD_IDS: Set<string> = (() => {
  const ids = new Set<string>();
  for (const line of Object.keys(DO_FEE_LINE_FIELDS) as FeeLineId[]) {
    const f = DO_FEE_LINE_FIELDS[line];
    if (f.feeDescription !== undefined) ids.add(String(f.feeDescription));
    if (f.payee !== undefined) ids.add(String(f.payee));
  }
  return ids;
})();

/** Clearing value for a previously-written grid field: blank for text
 *  (description/payee), $0.00 for money. */
export function clearingValueForFieldId(fieldId: string): string {
  return TEXT_WRITE_FIELD_IDS.has(fieldId) ? '' : '0.00';
}

/**
 * Stale-field clearing writes for a re-push (round-19 finding 2): a fee that
 * was previously pushed and has since gone $0/notApplicable — or whose new
 * writes no longer cover a previously-written field — must ZERO/BLANK the old
 * Encompass values, or the grid keeps stale amounts. `previousFieldIds` are
 * the ids recorded in the fee's prior write_back.fields; `currentWrites` is
 * the fee's entry in the NEW plan ([] when the fee is excluded entirely).
 */
export function buildClearingWrites(previousFieldIds: string[], currentWrites: GridWrite[]): GridWrite[] {
  const current = new Set(currentWrites.map((w) => w.fieldId));
  return previousFieldIds
    .filter((id) => !current.has(id))
    .map((id) => ({ fieldId: id, value: clearingValueForFieldId(id) }));
}

/**
 * Build the full write plan from the APPROVED fee set. Fees excluded: $0,
 * notApplicable, unapproved (callers pass only gated-in approved fees, but the
 * guard here is belt-and-braces). Manual fees resolve their line: previously
 * assigned line (write_back.assignedLine, passed via fee.line) wins; otherwise
 * the next empty misc line from the live grid read.
 */
export function buildWritebackPlan(
  fees: DocOrderFee[],
  liveGridValues: FieldValues,
  previouslyAssigned: Record<string, FeeLineId | undefined>,
  opts: WritebackOptions = {}
): WritebackPlan {
  const plan: WritebackPlan = { fees: [], unassigned: [] };
  const taken = new Set<FeeLineId>();

  // Occupied by Encompass-sourced fees first, so manual assignment can't collide.
  for (const fee of fees) {
    if (fee.source === 'encompass' && fee.line) taken.add(fee.line as FeeLineId);
  }
  for (const key of Object.values(previouslyAssigned)) {
    if (key) taken.add(key);
  }

  for (const fee of fees) {
    if (!(Number(fee.amount) > 0) || fee.notApplicable) continue;
    let line: FeeLineId | null = null;
    let assignedLine: FeeLineId | undefined;
    if (fee.source === 'encompass' && fee.line) {
      line = fee.line as FeeLineId;
    } else {
      const prior = previouslyAssigned[fee.feeKey];
      if (prior) {
        line = prior;
      } else {
        line = nextEmptyMiscLine(liveGridValues, taken);
        if (line) {
          assignedLine = line;
          taken.add(line);
        }
      }
    }
    if (!line) {
      plan.unassigned.push({ feeId: fee.id, name: fee.name });
      continue;
    }
    plan.fees.push({
      feeId: fee.id,
      feeKey: fee.feeKey,
      line,
      assignedLine,
      writes: buildFeeGridWrites(fee, line, opts),
    });
  }
  return plan;
}
